import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import FONT_ROBOTO_REGULAR from './pdf-font';

/**
 * EG-344 — Client-side export report theo CSV / Excel / PDF.
 *
 * Sử dụng:
 *   exportReport({
 *     filename: 'bao-cao-doanh-thu',
 *     format: 'excel',
 *     sheets: [
 *       { name: 'Tổng quan', columns: [...], rows: [...] },
 *       { name: 'Theo ngày',  columns: [...], rows: [...] },
 *     ],
 *   });
 */

export type ExportFormat = 'csv' | 'excel' | 'pdf';

export interface ExportColumn {
  /** Header text hiển thị trong file. */
  header: string;
  /** Key trong row object để lấy value. Ngoài ra có thể tự render qua `format`. */
  key: string;
  /** Optional formatter trước khi xuất (vd: VND, %). */
  format?: (value: unknown, row: Record<string, unknown>) => string | number;
}

export interface ExportSheet {
  /** Tên sheet (Excel) hoặc title (PDF page). CSV: ignored. */
  name: string;
  columns: ExportColumn[];
  rows: Array<Record<string, unknown>>;
}

export interface ExportInput {
  /** Tên file (không kèm extension). */
  filename: string;
  format: ExportFormat;
  sheets: ExportSheet[];
  /** Title hiển thị đầu PDF / metadata. Default = filename. */
  title?: string;
}

function toAOA(sheet: ExportSheet): Array<Array<string | number>> {
  const header = sheet.columns.map((c) => c.header);
  const body = sheet.rows.map((row) =>
    sheet.columns.map((col) => {
      const raw = row[col.key];
      if (col.format) {
        const out = col.format(raw, row);
        return out === undefined || out === null ? '' : out;
      }
      if (raw === undefined || raw === null) return '';
      if (typeof raw === 'number' || typeof raw === 'string') return raw;
      return String(raw);
    }),
  );
  return [header, ...body];
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function exportCsv(input: ExportInput) {
  // CSV chỉ ghi sheet đầu (CSV là single-sheet format).
  const sheet = input.sheets[0];
  if (!sheet) throw new Error('exportReport: cần ít nhất 1 sheet');
  const aoa = toAOA(sheet);
  const csv = aoa
    .map((row) =>
      row
        .map((cell) => {
          const s = String(cell ?? '');
          // Escape: nếu có dấu phẩy / ngoặc kép / xuống dòng → quote + double-quote.
          if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
          return s;
        })
        .join(','),
    )
    .join('\r\n');
  // BOM ﻿ để Excel mở UTF-8 chuẩn (tiếng Việt không bị mojibake).
  const blob = new Blob(['﻿', csv], { type: 'text/csv;charset=utf-8' });
  downloadBlob(blob, `${input.filename}.csv`);
}

function exportExcel(input: ExportInput) {
  const wb = XLSX.utils.book_new();
  for (const sheet of input.sheets) {
    const aoa = toAOA(sheet);
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    // Auto-size cột (heuristic theo độ dài cell tối đa).
    ws['!cols'] = sheet.columns.map((col, idx) => {
      const maxLen = aoa.reduce((m, row) => {
        const cell = String(row[idx] ?? '');
        return Math.max(m, cell.length);
      }, col.header.length);
      return { wch: Math.min(Math.max(maxLen + 2, 10), 60) };
    });
    XLSX.utils.book_append_sheet(wb, ws, sheet.name.slice(0, 31));
  }
  const out = XLSX.write(wb, { type: 'array', bookType: 'xlsx' });
  const blob = new Blob([out], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  downloadBlob(blob, `${input.filename}.xlsx`);
}

function exportPdf(input: ExportInput) {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
  // Register custom Roboto font for Vietnamese diacritics support
  const fontData = atob(FONT_ROBOTO_REGULAR);
  doc.addFileToVFS('Roboto-Regular.ttf', fontData);
  doc.addFont('Roboto-Regular.ttf', 'Roboto', 'normal', 'Identity-H');
  // autoTable renders header cells with fontStyle 'bold'; register the bold
  // style too (same Roboto TTF) so headers don't fall back to Helvetica-Bold,
  // which lacks Vietnamese diacritics and garbles column headers.
  doc.addFont('Roboto-Regular.ttf', 'Roboto', 'bold', 'Identity-H');
  doc.setFont('Roboto', 'normal');

  const title = input.title ?? input.filename;
  doc.setFontSize(14);
  doc.text(title, 40, 32);
  doc.setFontSize(9);
  doc.text(
    `Xuất lúc: ${new Date().toLocaleString('vi-VN')}`,
    40,
    48,
  );

  let cursorY = 64;
  for (const sheet of input.sheets) {
    if (cursorY > 500) {
      doc.addPage();
      cursorY = 40;
    }
    doc.setFontSize(11);
    doc.text(sheet.name, 40, cursorY);
    cursorY += 8;
    autoTable(doc, {
      startY: cursorY,
      head: [sheet.columns.map((c) => c.header)],
      body: sheet.rows.map((row) =>
        sheet.columns.map((col) => {
          const raw = row[col.key];
          if (col.format) {
            const v = col.format(raw, row);
            return v === undefined || v === null ? '' : String(v);
          }
          if (raw === undefined || raw === null) return '';
          return String(raw);
        }),
      ),
      styles: { fontSize: 9, font: 'Roboto' },
      headStyles: { fillColor: [164, 25, 61] },
      margin: { left: 40, right: 40 },
    });
    // jsPDF AutoTable — last Y position
    cursorY = (doc as unknown as { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY ?? cursorY;
    cursorY += 24;
  }

  doc.save(`${input.filename}.pdf`);
}

export function exportReport(input: ExportInput) {
  switch (input.format) {
    case 'csv':
      return exportCsv(input);
    case 'excel':
      return exportExcel(input);
    case 'pdf':
      return exportPdf(input);
  }
}

/**
 * Helper format VND cho cột number (export không decimal).
 */
export const formatNumberCell = (v: unknown) => {
  const n = typeof v === 'number' ? v : Number(v);
  if (Number.isNaN(n)) return '';
  return n.toLocaleString('vi-VN');
};

/**
 * Helper format ngày YYYY-MM-DD → DD/MM/YYYY.
 */
export const formatDateCell = (v: unknown) => {
  if (typeof v !== 'string') return '';
  const m = v.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return v;
  return `${m[3]}/${m[2]}/${m[1]}`;
};
